import { useState, useEffect, useCallback, lazy, Suspense, type ReactNode } from 'react';
import { LoadingState } from '@/components/ui/States';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Ambulance, BedDouble, CalendarDays, ChartColumn, FileCheck2, FileText, FlaskConical, GraduationCap, House, MapPin, Mic, Pill, Power, Receipt, RefreshCw, Stethoscope, Ticket, Wrench, type LucideIcon } from 'lucide-react';
import { TopbarFocusProvider } from './lib/topbarFocus';
import { useBranding, DEFAULT_ICON_URL } from './hooks/useBranding';
import { useAuth } from './hooks/useAuth';
import { loadConfig, getActiveProvider } from './aiConfig';
import { getTodayQueue } from './api';
import type { QueueItem } from './features/queue/types';

// Existing modules
import Dashboard           from './features/dashboard/Dashboard'; // rebuilt to docs/design/dashboard (2026-10)
import QueueView           from './features/queue/QueueView'; // first screen migrated — see HMS_V4_API_CONTRACT.md
import ConsultationModule  from './features/consultation/ConsultationModule'; // migrated — see HMS_V4_API_CONTRACT.md
import PharmacyModule, { type PharmacyTab } from './features/pharmacy/PharmacyModule'; // migrated — see HMS_V4_API_CONTRACT.md
import BillingModule       from './features/billing/BillingModule'; // migrated — see HMS_V4_API_CONTRACT.md
import ReportsModule       from './features/reports/ReportsModule'; // migrated — see HMS_V4_API_CONTRACT.md
import PatientRecords      from './components/PatientRecords';
import Login               from './components/Login';
import AdminModule from './features/admin/AdminModule'; // migrated — see HMS_V4_API_CONTRACT.md
import AdminBreadcrumbs from './features/admin/AdminBreadcrumbs';
import { isAdminPath, navigateAdmin, notifyPathChange } from './features/admin/adminRoute';
import { ADMIN_ROOT } from './features/admin/adminRegistry';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/Dialog';
import { Toaster } from './components/ui/Toaster';
import { CommandPalette } from './features/quick-actions/CommandPalette'; // Ctrl+K: search + quick actions
import { QuickActionsMenu } from './features/quick-actions/QuickActionsMenu'; // topbar lightning menu, Alt+Q / Alt+1..9
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from './components/ui/DropdownMenu';
import { NotificationBell } from './features/notifications/NotificationBell';
import { FeedbackWidget } from './features/feedback/FeedbackWidget'; // right-edge "Feedback" tab, every role
import { CopilotLauncher, COPILOT_BUTTON_EVENT, COPILOT_BUTTON_PREF, useUiFlag } from './features/copilot/CopilotLauncher'; // G2 bottom-right chatbot
import type { CopilotLink } from './features/copilot/api';
import { openFeedback } from './features/feedback/api';
import type { NotificationLink } from './features/notifications/types';
import { TwoStepDialog } from './features/two-step/TwoStepDialog';
import { MyAiPreferencesPanel } from './features/ai-assist/MyAiPreferencesPanel';

// New v4 modules
// VisitWorkflow.jsx is deliberately still legacy — see CLAUDE.md's ABDM/UI
// sequencing note: the Consultation screen and its internals convert last,
// after the deep consultation rebuild targets the new clinical tables.
// allowJs resolves this import with an inferred (not any) shape.
import VisitWorkflow          from './components/VisitWorkflow';
import VoiceIntake            from './components/VoiceIntake';
import IPModule, { type OpenAdmissionRequest } from './components/IPModule';
import type { AdmissionListRow } from './features/ip-admissions/api';
import AppointmentsModule      from './features/appointments/AppointmentsModule'; // migrated — see HMS_V4_API_CONTRACT.md
import LaboratoryModule        from './features/laboratory/LaboratoryModule'; // the LIS — masters, orders, samples, structured results, reports
import { ClaimsModule }        from './features/claims/ClaimsModule'; // insurance claims desk — worklist + workspace (Insurance Claims Phase 4)
import EmergencyModule         from './features/emergency/EmergencyModule'; // Emergency / Casualty — tracking board, triage, ED encounter workspace
import DoctorAlertPopup        from './features/emergency/DoctorAlertPopup'; // ED page to the attending doctor — shown on every screen
// HMS User Guide & Learning Center — every role. Loaded on first open: it is
// mostly guide text, which no other screen needs in the main bundle.
const LearningCenterModule = lazy(() => import('./features/learning-center/LearningCenterModule'));

// Which modules a role can see is now admin-configurable (see Admin Panel's
// Roles tab, backed by the `roles` table) instead of hardcoded per-nav-item
// role arrays here — NAV only carries each module's *display* metadata
// (icon, label, section); access itself comes from user.modules, populated
// at login/session-restore from the user's role's row in that table.
// Icons are lucide components (chrome icons, per the UI assessment's icon
// policy, backlog #14) — the 2026-10 dashboard redesign replaced the emoji.
// `always` marks the one module shown to every signed-in user regardless of
// role: the Learning Center holds documentation only, no patient or hospital
// data, and each guide's own "Open …" button still checks the reader's modules.
const NAV: Array<{ id: string; icon: LucideIcon; label: string; section: string; always?: boolean }> = [
  { id:'dashboard',      icon:House,        label:'Dashboard',         section:'main' },
  { id:'voice',          icon:Mic,          label:'Intake',            section:'opd' },
  { id:'visit',          icon:RefreshCw,    label:'Visit workflow',    section:'opd' },
  { id:'queue',          icon:Ticket,       label:'Queue',             section:'opd' },
  { id:'appointments',   icon:CalendarDays, label:'Appointments',      section:'opd' },
  { id:'consultation',   icon:Stethoscope,  label:'Consultation',      section:'opd' },
  { id:'emergency',      icon:Ambulance,    label:'Emergency',         section:'emergency' },
  { id:'records',        icon:FileText,     label:'Patient records',   section:'records' },
  { id:'ip',             icon:BedDouble,    label:'IP / Wards',        section:'ip' },
  { id:'laboratory',     icon:FlaskConical, label:'Laboratory',        section:'laboratory' },
  { id:'pharmacy',       icon:Pill,         label:'Pharmacy',          section:'pharmacy' },
  { id:'billing',        icon:Receipt,      label:'Billing',           section:'billing' },
  { id:'claims:desk',    icon:FileCheck2,   label:'Claims desk',       section:'billing' },
  { id:'reports',        icon:ChartColumn,  label:'Reports',           section:'admin' },
  { id:'admin-panel',    icon:Wrench,       label:'Admin Panel',       section:'admin' },
  { id:'learning',       icon:GraduationCap, label:'Learning Center',  section:'help', always:true },
];

const NAV_LABELS: Record<string, string> = Object.fromEntries(NAV.map(n => [n.id, n.label]));

const SEC_LABELS: Record<string, string> = { main:'', emergency:'Emergency', opd:'OPD workflow', records:'Records', ip:'Inpatient', laboratory:'Laboratory', pharmacy:'Pharmacy', billing:'Billing', admin:'Admin & settings', help:'Help' };

const MOD_TITLES: Record<string, string> = {
  dashboard:'Dashboard', visit:'Visit workflow', voice:'Patient intake',
  queue:'Queue management', appointments:'Appointments',
  consultation:'AI-assisted consultation', records:'Patient records & documents',
  ip:'IP / Wards', laboratory:'Laboratory', emergency:'Emergency / Casualty',
  pharmacy:'Pharmacy', billing:'OPD billing', reports:'Reports & analytics',
  'admin-panel':'Admin panel', 'claims:desk':'Claims desk', learning:'Learning Center',
};

const initialParams   = new URLSearchParams(window.location.search);
const initialRecordsUhid = initialParams.get('records');

// ── Sidebar responsive breakpoints ──────────────────────────────
// The collapse/expand toggle next to the logo always works, at any
// desktop/tablet width — it directly sets and persists collapsedPref.
// Width only supplies the *first-ever* default (collapsed on a narrower
// screen, expanded on a wide one) before the user has touched the toggle.
// <PHONE: a fixed rail doesn't fit at all — the sidebar becomes an
// off-canvas drawer opened via a hamburger button in the topbar instead.
const PHONE_BP  = 640;
const NARROW_BP = 1100;
const SIDEBAR_COLLAPSED_KEY = 'hms_sidebar_collapsed';

function useViewportWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

export default function App() {
  const { user, checking, logout, switchBranch, branchVersion } = useAuth();
  // The Admin Panel is the one module whose position lives in the URL
  // (/admin/<section>/<setting> — features/admin/adminRoute.tsx), so a refresh
  // or a shared link on an /admin path opens it directly.
  const [mod, setMod]                         = useState(isAdminPath() ? 'admin-panel' : initialRecordsUhid ? 'records' : 'dashboard');
  // Set by a module showing a full-page detail view (lib/topbarFocus.tsx).
  const [topbarHidden, setTopbarHidden]       = useState(false);
  // A module's own content for the topbar's left slot (lib/topbarFocus.tsx) — the Dashboard's greeting and clock.
  const [topbarContent, setTopbarContent]     = useState<ReactNode | null>(null);
  const [activeQueueItem, setActiveQueueItem] = useState<QueueItem | null>(null);
  const [aiCfg, setAiCfg]                     = useState(loadConfig);
  const [recordsUhid, setRecordsUhid]         = useState<string | null>(initialRecordsUhid);
  const [viewRecordUhid, setViewRecordUhid]   = useState<string | null>(null); // in-place patient-record viewer (Dialog), for mid-task lookups that shouldn't lose the current screen
  const [visitPreselectId, setVisitPreselectId] = useState<string | null>(null); // from Dashboard global search — opens that visit read-only
  const [billPreselectId, setBillPreselectId]   = useState<number | null>(null); // from Dashboard global search — opens that bill's PDF
  const [startPatient, setStartPatient]         = useState<unknown>(null); // from Appointments' "Start intake" — begins that patient's visit directly
  const [ipOpenAdmission, setIpOpenAdmission]   = useState<OpenAdmissionRequest | null>(null); // Patient Records → Admissions → Open case sheet
  const clearIpOpenAdmission = useCallback(() => setIpOpenAdmission(null), []);
  // Opens an admission's Case Sheet in the IP module; "back" returns to the record it came from.
  const openAdmissionFromRecords = (row: AdmissionListRow, back: () => void) => {
    setIpOpenAdmission({ id: row.id, uhid: row.patient_uhid, admitted: row.status === 'admitted', back: { label: 'Back to patient record', onBack: back } });
    setMod('ip'); setMobileNavOpen(false);
  };
  const [openVisitCode, setOpenVisitCode]       = useState<string | null>(null); // from Consultation's patient list — opens that visit live (editable), not read-only
  const [erOpenEncounterId, setErOpenEncounterId] = useState<number | null>(null); // from the ED page popup's Accept — opens that ED encounter
  const [waitingCount, setWaitingCount]       = useState(0);
  // The topbar power button confirms in a dialog: window.confirm() is silently
  // refused by embedded browsers (the desktop app's pane, Electron), which made
  // the button do nothing at all.
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [showTwoStep, setShowTwoStep] = useState(false); // Phase 1 G20 — profile menu → Two-step login
  const [showAiPreferences, setShowAiPreferences] = useState(false); // Doctor AI Memory (Phase 2) — "My AI preferences", from the profile menu
  const [collapsedPref, setCollapsedPref]     = useState(() => {
    try { const v = localStorage.getItem(SIDEBAR_COLLAPSED_KEY); if (v !== null) return v === '1'; } catch {}
    return window.innerWidth < NARROW_BP;
  });
  const [mobileNavOpen, setMobileNavOpen]     = useState(false);
  const [hovering, setHovering]               = useState(false);
  // Admin Panel folds the primary sidebar into a single breadcrumb (see
  // CLAUDE.md's Admin Panel navigation note) instead of showing two nav
  // columns side by side — AdminModule reports its currently open
  // group/setting here so the breadcrumb can read one continuous trail:
  // "ClintoBytes Health > Admin Panel > <group> > <setting>".

  const width      = useViewportWidth();
  const isPhone    = width < PHONE_BP;
  // The visit workflow is the one screen where horizontal space directly
  // trades off against room for the doctor's own data (rail + chat/
  // prescription split) — auto-collapsing here, on top of the sticky
  // preference, doesn't touch collapsedPref itself, so leaving the visit
  // always reverts to whatever the user had actually chosen. Hovering still
  // peeks the full labeled rail (see below) if they need a label mid-visit.
  // Admin Panel has its own persistent settings rail (AdminModule) — showing
  // the primary app sidebar alongside it would stack two nav columns side by
  // side. Folded into the topbar's breadcrumb instead, desktop-only: on
  // phone the sidebar is already an off-canvas drawer, not a second
  // permanent column, so there is nothing to fold there.
  const suppressSidebarForAdmin = mod === 'admin-panel' && !isPhone;
  const autoCollapseForVisit = mod === 'visit' && !isPhone;
  const collapsed  = !isPhone && (collapsedPref || autoCollapseForVisit);
  // While collapsed, hovering the rail temporarily expands it (full width +
  // labels) without touching the sticky preference — moving the cursor away
  // reverts to the icon-only rail.
  const visuallyCollapsed = collapsed && !hovering;
  const toggleCollapsed = () => setCollapsedPref(c => { const next = !c; try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch {} return next; });
  const sidebarWidth = visuallyCollapsed ? 64 : 224;

  const { branding } = useBranding();  // injects CSS vars globally
  const activeProvider = getActiveProvider(aiCfg);
  const visibleNav = NAV.filter(n => n.always || user?.modules?.includes(n.id));
  // Which Pharmacy tab to open on; null = its default. Set by links that mean
  // a specific tab (stock alerts → Inventory), cleared by ordinary navigation.
  const [pharmacyTab, setPharmacyTab] = useState<PharmacyTab | null>(null);
  // The Dashboard's wait-time insights page, and the "back to it" link a visit
  // opened from there carries. Ordinary navigation clears both.
  const [dashboardPage, setDashboardPage] = useState<'home' | 'wait-insights'>('home');
  const [visitBackLink, setVisitBackLink] = useState<{ label: string; onClick: () => void } | null>(null);
  // A visit opened only to look at it (from the insights page): read-only whatever its stage.
  const [reviewVisitCode, setReviewVisitCode] = useState<string | null>(null);
  useEffect(() => { if (mod !== 'visit') setVisitBackLink(null); }, [mod]);
  const selectMod = (id: string) => { setPharmacyTab(null); setDashboardPage('home'); setVisitBackLink(null); setMod(id); setMobileNavOpen(false); };
  const openPharmacyTab = (tab: PharmacyTab) => { setPharmacyTab(tab); setMod('pharmacy'); setMobileNavOpen(false); };
  // The topbar bell's items name the module that resolves them (contract §6k);
  // the ones with a deep link reuse the same hand-off props as elsewhere here.
  const openNotificationLink = (link: NotificationLink) => {
    if (!link.module || !user?.modules?.includes(link.module)) return;
    if (link.module === 'emergency' && link.encounter_id) setErOpenEncounterId(link.encounter_id);
    if (link.module === 'visit' && link.visit_code) setOpenVisitCode(link.visit_code);
    if (link.module === 'pharmacy' && link.tab) { openPharmacyTab(link.tab as PharmacyTab); return; }
    selectMod(link.module);
  };

  // Sidebar "waiting" badge — lightweight periodic refresh (no realtime infra in this app).
  // GET /api/queue/today is module-gated server-side (queue/consultation/
  // dashboard/visit — see routes/queue.js); mirrored here so a role with
  // none of those (Pharmacist, Executive Director as currently seeded)
  // doesn't poll a 403 every 30s for a badge it will never show a number in.
  const canSeeQueueBadge = !!user?.modules?.some(m => ['queue', 'consultation', 'dashboard', 'visit'].includes(m));
  useEffect(() => {
    if (!user || !canSeeQueueBadge) return;
    const refresh = () => getTodayQueue().then((q: QueueItem[]) => setWaitingCount(q.filter(i => i.status === 'Waiting').length)).catch(() => {});
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [user, branchVersion, canSeeQueueBadge]);

  // If the current page isn't allowed for this role (e.g. role changed, or a
  // stale deep link), fall back to the dashboard instead of a blank page.
  useEffect(() => {
    if (user && !visibleNav.some(n => n.id === mod)) setMod('dashboard');
  }, [user, mod, visibleNav]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the URL in step with the module, one direction only (mod → URL):
  // entering the Admin Panel pushes /admin, leaving it pushes / — so Back
  // returns across the boundary — and every other module just records
  // itself on the current history entry, which is what popstate below
  // restores when Back lands on it.
  useEffect(() => {
    const onAdmin = isAdminPath();
    if (mod === 'admin-panel') {
      if (!onAdmin) navigateAdmin(ADMIN_ROOT);
    } else if (onAdmin) {
      window.history.pushState({ hmsMod: mod }, '', '/');
      notifyPathChange();
    } else {
      window.history.replaceState({ ...(window.history.state ?? {}), hmsMod: mod }, '');
    }
  }, [mod]);

  // Back/Forward. Moves within the Admin Panel are handled by its own route
  // hook; this only switches the module when the boundary is crossed.
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      if (isAdminPath()) { setMod('admin-panel'); return; }
      const wanted = (e.state as { hmsMod?: string } | null)?.hmsMod;
      setMod(m => wanted ?? (m === 'admin-panel' ? 'dashboard' : m));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const handleStartConsult = (item: QueueItem) => { setActiveQueueItem(item); setMod('consultation'); };
  // Opens PatientRecords in-place, in a Dialog — previously this opened a whole
  // second copy of the app in a new tab (full reload, no way back but closing
  // the tab). A mid-consult lookup is a quick peek, not a context switch: the
  // Dialog keeps whatever's in local component state (in-progress chat draft,
  // unsent prescription edits) untouched underneath it.
  const viewPatientRecord  = (uhid: string) => setViewRecordUhid(uhid);
  // Where a Copilot chip or card sends the user (G2). The server only offers screens the user's role can open.
  const [copilotButtonHidden, setCopilotButtonHidden] = useUiFlag(COPILOT_BUTTON_PREF);
  const openCopilotLink = (link: CopilotLink) => {
    if (link.record_uhid) { viewPatientRecord(link.record_uhid); return; }
    if (link.admin) { navigateAdmin(link.admin); selectMod('admin-panel'); return; }
    if (link.module) openNotificationLink({ module: link.module, tab: link.tab });
  };
  const viewPatientRecordInNewTab = (uhid: string) => window.open(`${window.location.origin}?records=${encodeURIComponent(uhid)}`, '_blank');

  if (checking) {
    return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', color:'#6B7280', fontSize:13 }}>Loading…</div>;
  }
  if (!user) {
    return <Login />;
  }

  const canOpenAdmin = !!user.modules?.includes('admin-panel');
  const sidebarOpen  = isPhone ? mobileNavOpen : true;

  const sidebar = (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={isPhone ? 'app-sidebar-phone' : undefined}
      style={{
      // var(--navy-dark), not --navy — same hue as the hospital's chosen
      // navy_color, forced dark (see useBranding.ts's injectCSS / lib/color.ts's
      // ensureDark). --navy itself isn't guaranteed dark (a hospital can pick
      // a bright or pastel navy_color), and this permanently-visible rail
      // needs to read as fixed chrome regardless — VisitStepper's compact
      // bar made the same switch, for the same reason.
      width: isPhone ? 224 : sidebarWidth, background:'var(--navy-dark)', display:'flex', flexDirection:'column',
      flexShrink:0, transition:'width .18s ease', overflow:'hidden',
      ...(isPhone ? {
        position:'fixed', top:0, left:0, zIndex:40,
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition:'transform .2s ease',
      } : {}),
    }}>
      {/* Logo / name + collapse toggle — the toggle is hidden while visually
          collapsed (only a centered favicon shows); hovering reveals the
          full header, toggle included, so pinning back open is still reachable. */}
      <div style={{ padding: visuallyCollapsed ? '16px 0 12px' : '16px 18px 12px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'flex-start', justifyContent: visuallyCollapsed ? 'center' : 'space-between', gap:6 }}>
        <div style={{ cursor: canOpenAdmin ? 'pointer' : 'default', minWidth:0, ...(visuallyCollapsed ? { textAlign:'center' } : { flex:1 }) }}
          onClick={() => { if (canOpenAdmin) selectMod('admin-panel'); }}>
          {visuallyCollapsed
            ? (branding.favicon_url || branding.logo_url
                ? <img src={branding.favicon_url || branding.logo_url || undefined} alt="logo" style={{ height:28, width:28, objectFit:'contain', display:'block', margin:'0 auto' }} />
                : <img src={DEFAULT_ICON_URL} alt="logo" style={{ height:28, width:28, objectFit:'contain', display:'block', margin:'0 auto', background:'#fff', borderRadius:8, padding:2 }} />)
            : (branding.logo_url
                ? <img src={branding.logo_url} alt="logo" style={{ height:32, objectFit:'contain', marginBottom:4 }} />
                : <img src={DEFAULT_ICON_URL} alt="logo" style={{ height:32, width:32, objectFit:'contain', marginBottom:4, background:'#fff', borderRadius:8, padding:2 }} />)}
          {!visuallyCollapsed && (
            <>
              <div style={{ fontSize:14, fontWeight:700, color:'#fff', lineHeight:1.3, marginTop:4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{branding.hospital_name}</div>
              <div style={{ fontSize:9, color:'var(--accent-on-navy)', opacity:.85, letterSpacing:'1.2px', textTransform:'uppercase' }}>{branding.tagline}</div>
            </>
          )}
        </div>
        {!visuallyCollapsed && (
          <button
            onClick={() => isPhone ? setMobileNavOpen(false) : toggleCollapsed()}
            title={isPhone ? 'Close menu' : (collapsed ? 'Expand sidebar' : 'Collapse sidebar to icons')}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
            style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'rgba(255,255,255,0.7)', borderRadius:'50%', width:24, height:24, fontSize:12, cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', transition:'background .15s ease, color .15s ease' }}>
            {isPhone ? '✕' : (
              /* Same glyph, rotated — points left when open ("collapse this
                 way"), flips to point right when collapsed ("expand this
                 way"), animating smoothly between the two instead of
                 swapping to an unrelated icon. Previously always pointed
                 left regardless of state, which was backwards while
                 hover-peeking a collapsed rail. */
              <span style={{ display:'inline-block', transition:'transform .2s ease', transform: collapsed ? 'rotate(180deg)' : 'none' }}>‹</span>
            )}
          </button>
        )}
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'8px 0' }}>
        {/*
          Section render order. A NAV item whose `section` is missing from this
          list is silently dropped — it never matches any group, so it renders
          nowhere even though it is in visibleNav and the role holds the grant.
          Adding a NAV section means adding it here AND to SEC_LABELS above.
        */}
        {['main','emergency','opd','records','ip','laboratory','pharmacy','billing','admin','help'].map(sec => {
          const items = visibleNav.filter(n => n.section === sec);
          if (!items.length) return null;
          return (
            <div key={sec}>
              {SEC_LABELS[sec] && !visuallyCollapsed && (
                <div style={{ padding:'10px 18px 4px', fontSize:9, letterSpacing:'1.5px', color:'rgba(255,255,255,0.55)', textTransform:'uppercase', fontWeight:700 }}>
                  {SEC_LABELS[sec]}
                </div>
              )}
              {items.map(item => (
                <div key={item.id} className={`nav-item ${mod===item.id?'active':''}`} title={visuallyCollapsed ? item.label : undefined}
                  onClick={() => selectMod(item.id)}
                  style={visuallyCollapsed ? { justifyContent:'center', padding:'10px 0', position:'relative' } : undefined}>
                  {/* Fixed-width icon slot — emoji glyphs render at different
                      natural widths (e.g. ⬛/🎙 narrower than 🔄/🩺), which
                      otherwise shifts where each label starts. */}
                  <item.icon aria-hidden className="nav-icon h-[17px] w-[17px] shrink-0" />
                  {!visuallyCollapsed && <span>{item.label}</span>}
                  {item.id === 'queue' && waitingCount > 0 && (
                    <span style={visuallyCollapsed
                      ? { position:'absolute', top:4, right:14, background:'var(--color-danger)', color:'#fff', borderRadius:4, minWidth:14, height:14, fontSize:9, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }
                      : { background:'var(--color-danger)', color:'#fff', borderRadius:4, padding:'0 6px', lineHeight:'17px', fontSize:10.5, fontWeight:700 }}>{waitingCount}</span>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* User identity + Sign out moved to the topbar's UserMenu (see below) —
          per an explicit request during the Dashboard modernization pass to
          match a client-supplied dashboard mockup's top-right avatar/name/
          role/dropdown placement. The sidebar footer used to own this; it's
          now just the nav rail, ending at the last section above. */}
    </div>
  );

  return (
    <>
    <div className="app-shell" style={{ display:'flex', overflow:'hidden' }}>

      {!suppressSidebarForAdmin && sidebar}
      {isPhone && mobileNavOpen && (
        <div onClick={() => setMobileNavOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:30 }} />
      )}

      {/* ── Main content ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        {/* Topbar — suppressed on desktop/tablet for the visit workflow: its
            own sticky bar (VisitStepper) already carries the visit's identity
            (Visit ID) and the close/rollback actions, so this row would only
            be duplicate chrome eating vertical space a doctor needs for the
            visit's actual data. Kept on phone regardless — it's the only way
            to reach the hamburger menu there, and the phone stepper is
            static (not sticky), so the space tradeoff doesn't apply the same way.
            Same rule for an open claim or IP admission (`topbarHidden`, set via
            lib/topbarFocus.tsx): each has its own sticky identity header. */}
        {!((mod === 'visit' || topbarHidden) && !isPhone) && (
        <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding: isPhone ? '10px 14px' : '12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, gap:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
            {isPhone && (
              <button onClick={() => setMobileNavOpen(true)} title="Open menu"
                style={{ background:'var(--bg)', border:'1px solid #E5E7EB', borderRadius:8, width:34, height:34, fontSize:15, flexShrink:0 }}>☰</button>
            )}
            {suppressSidebarForAdmin ? (
              <div style={{ display:'flex', alignItems:'center', gap:6, minWidth:0, fontSize:14, overflow:'hidden' }}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      title="Switch module"
                      style={{ display:'flex', alignItems:'center', gap:7, background:'none', border:'none', margin:'0 -6px 0 -2px', padding:'4px 6px', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:700, color:'#111827' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                      {branding.favicon_url || branding.logo_url
                        ? <img src={branding.favicon_url || branding.logo_url || undefined} alt="" style={{ height:18, width:18, objectFit:'contain', flexShrink:0 }} />
                        : <img src={DEFAULT_ICON_URL} alt="" style={{ height:18, width:18, objectFit:'contain', flexShrink:0 }} />}
                      <span style={{ maxWidth:160, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{branding.hospital_name}</span>
                      <span style={{ fontSize:10, color:'#9CA3AF', fontWeight:400 }}>▾</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" style={{ minWidth:220 }}>
                    {['main','emergency','opd','records','ip','laboratory','pharmacy','billing','admin','help'].map(sec => {
                      const items = visibleNav.filter(n => n.section === sec && n.id !== 'admin-panel');
                      if (!items.length) return null;
                      return (
                        <div key={sec}>
                          {SEC_LABELS[sec] && <DropdownMenuLabel style={{ fontSize:10, letterSpacing:'.06em', textTransform:'uppercase', color:'#9CA3AF' }}>{SEC_LABELS[sec]}</DropdownMenuLabel>}
                          {items.map(item => (
                            <DropdownMenuItem key={item.id} onClick={() => selectMod(item.id)}>
                              <item.icon aria-hidden className="h-4 w-4 text-ink-muted" /> {item.label}
                            </DropdownMenuItem>
                          ))}
                        </div>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span style={{ color:'#9CA3AF' }}>›</span>
                <AdminBreadcrumbs />
              </div>
            ) : (
              mod === 'admin-panel'
                ? <AdminBreadcrumbs compact />
                : topbarContent ?? <div style={{ fontSize: isPhone ? 15 : 18, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{MOD_TITLES[mod]}</div>
            )}
          </div>
          {/* Assessment backlog #9 — "Find anything" moved out of the
              dashboard body into the topbar, reachable from every screen
              instead of only the Dashboard. On a phone, where there is no
              room for the full field beside the hamburger button, it shrinks
              to a search icon — the one way to find a patient there, since
              Patient records has no search of its own. */}
          <div style={{ flexShrink:0 }}>
            <CommandPalette
              compact={isPhone}
              modules={user.modules}
              role={user.role}
              onNavigate={selectMod}
              onNavigateToSetting={path => { navigateAdmin(path); selectMod('admin-panel'); }}
              onNavigateToPatient={uhid => { setRecordsUhid(uhid); setMod('records'); }}
              onNavigateToVisit={visitId => { setVisitPreselectId(visitId); setMod('visit'); }}
              onNavigateToBill={billId => { setBillPreselectId(billId); setMod('billing'); }}
            />
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
            {/* Quick actions from every screen (docs/design/dashboard/QuickActions_Options.html, option B). */}
            {!isPhone && <QuickActionsMenu modules={user.modules} onNavigate={selectMod} />}
            {/* Hidden entirely for a single-branch deployment — the whole
                point of Phase 4a's default-branch backfill was that nothing
                looks different until a second branch actually exists. */}
            {(user.branches?.length ?? 0) > 1 && (
              <label className="relative flex h-9 items-center rounded-lg border border-control-line bg-card pl-2.5 pr-1 text-[12.5px] font-medium text-ink" title="Switch branch">
                <MapPin aria-hidden className="h-4 w-4 shrink-0 text-teal" />
                <select
                  value={user.active_branch?.id || ''}
                  onChange={e => switchBranch(e.target.value)}
                  aria-label="Branch"
                  className="w-auto cursor-pointer border-0 bg-transparent py-0 pl-2 pr-1 text-[12.5px] font-medium text-ink shadow-none outline-none">
                  {user.branches!.map(b => (
                    <option key={b.id} value={b.id}>{isPhone ? b.code : b.name}</option>
                  ))}
                </select>
              </label>
            )}
            {mod === 'consultation' && !isPhone && (
              <div style={{ padding:'4px 12px', background:activeProvider.bg, border:`1px solid ${activeProvider.border}`, borderRadius:20, fontSize:12, fontWeight:600, color:activeProvider.color }}>
                {activeProvider.icon} {activeProvider.name}
              </div>
            )}
            <NotificationBell onNavigate={openNotificationLink} />
            {/* User identity + Sign out — moved here from the sidebar footer
                per an explicit request during the Dashboard modernization
                pass, to match a client-supplied mockup's top-right avatar/
                name/role/dropdown placement. Shown at every width (unlike
                CommandPalette, hidden on phone for space) since Sign Out is
                the one thing every session needs reachable, not just a
                convenience. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  style={{ display:'flex', alignItems:'center', gap:8, background:'none', border:'none', padding:'2px 4px 2px 2px', borderRadius:20, cursor:'pointer' }}>
                  <div style={{ width:30, height:30, borderRadius:'50%', background:'var(--teal)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0 }}>
                    {user.full_name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  {!isPhone && (
                    <div style={{ textAlign:'left', minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#111827', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:140 }}>{user.full_name}</div>
                      <div style={{ fontSize:10, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.3px' }}>{user.role}</div>
                    </div>
                  )}
                  {!isPhone && <span style={{ fontSize:10, color:'#9CA3AF' }}>▾</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div style={{ fontWeight:600 }}>{user.full_name}</div>
                  <div style={{ fontSize:11, color:'#6B7280', fontWeight:400 }}>{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowTwoStep(true)}>🔐 Two-step login</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowAiPreferences(true)}>🧠 My AI preferences</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openFeedback()}>💬 Send feedback</DropdownMenuItem>
                {user.modules?.includes('copilot') && (
                  <DropdownMenuItem onClick={() => {
                    setCopilotButtonHidden(!copilotButtonHidden);
                    window.dispatchEvent(new CustomEvent(COPILOT_BUTTON_EVENT, { detail: { show: copilotButtonHidden } }));
                  }}>{copilotButtonHidden ? '✨ Show Copilot button' : '✨ Hide Copilot button (Alt + / still opens it)'}</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>⏻ Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!isPhone && (
              <button type="button" title="Sign out" aria-label="Sign out"
                onClick={() => setConfirmSignOut(true)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-muted hover:text-ink">
                <Power className="h-[18px] w-[18px]" />
              </button>
            )}
          </div>
        </div>
        )}

        {/* Page content */}
        <TopbarFocusProvider setHidden={setTopbarHidden} setContent={setTopbarContent}>
        <div style={{ flex:1, overflow:'auto', padding: mod==='visit'?0:(isPhone ? '14px' : '20px 24px') }}>
          {mod === 'dashboard'     && <Dashboard
            onOpenVisit={visitCode => { setVisitBackLink(null); setOpenVisitCode(visitCode); setMod('visit'); }}
            page={dashboardPage}
            onPageChange={setDashboardPage}
            onOpenVisitFromInsights={visitCode => {
              setVisitBackLink({ label: 'Back to wait-time insights', onClick: () => { setVisitBackLink(null); setDashboardPage('wait-insights'); setMod('dashboard'); } });
              setReviewVisitCode(visitCode); setMod('visit');
            }}
            onNavigateToModule={selectMod}
            onOpenStock={() => openPharmacyTab('inventory')}
            onViewPatientRecord={viewPatientRecord}
          />}
          {mod === 'visit'         && <VisitWorkflow onVisitComplete={(v: unknown)=>console.log('Visit done',v)} onViewPatientRecord={viewPatientRecord} preselectVisitId={visitPreselectId} onPreselectConsumed={()=>setVisitPreselectId(null)} startPatient={startPatient} onStartPatientConsumed={()=>setStartPatient(null)} openVisitId={openVisitCode} onOpenVisitConsumed={()=>setOpenVisitCode(null)} backLink={visitBackLink} reviewVisitId={reviewVisitCode} onReviewConsumed={()=>setReviewVisitCode(null)} />}
          {mod === 'voice'         && <VoiceIntake  onIntakeComplete={()=>{setMod('visit');}} />}
          {mod === 'queue'         && <QueueView    onStartConsult={handleStartConsult} onOpenVisit={visitCode => { setOpenVisitCode(visitCode); setMod('visit'); }} />}
          {mod === 'appointments'  && <AppointmentsModule onStartVisit={patient => { setStartPatient(patient); setMod('visit'); }} />}
          {mod === 'consultation'  && <ConsultationModule activeQueueItem={activeQueueItem} onViewPatientRecord={viewPatientRecord} onOpenVisit={visitCode => { setOpenVisitCode(visitCode); setMod('visit'); }} />}
          {mod === 'records'       && <PatientRecords preselectUhid={recordsUhid ?? undefined} onPreselectConsumed={()=>setRecordsUhid(null)} onOpenVisit={visitCode => { setVisitPreselectId(visitCode); setMod('visit'); }}
            onOpenAdmission={row => openAdmissionFromRecords(row, () => { setRecordsUhid(row.patient_uhid); setMod('records'); })} />}
          {mod === 'ip'            && <IPModule onViewPatientRecord={viewPatientRecord} openAdmission={ipOpenAdmission} onOpenAdmissionConsumed={clearIpOpenAdmission} />}
          {mod === 'laboratory'    && <LaboratoryModule />}
          {mod === 'claims:desk'   && <ClaimsModule />}
          {mod === 'emergency'     && <EmergencyModule openEncounterId={erOpenEncounterId} onOpenConsumed={() => setErOpenEncounterId(null)} />}
          {mod === 'pharmacy'      && <PharmacyModule key={pharmacyTab ?? 'default'} initialTab={pharmacyTab ?? undefined} />}
          {mod === 'billing'       && <BillingModule preselectBillId={billPreselectId} onPreselectConsumed={()=>setBillPreselectId(null)} />}
          {mod === 'reports'       && <ReportsModule />}
          {mod === 'learning'      && (
            <Suspense fallback={<LoadingState label="Loading the Learning Center…" />}>
              <LearningCenterModule
                modules={user.modules ?? []}
                role={user.role}
                moduleLabels={NAV_LABELS}
                onOpenModule={selectMod}
                onOpenAdmin={path => { navigateAdmin(path); selectMod('admin-panel'); }}
              />
            </Suspense>
          )}
          {mod === 'admin-panel'   && <AdminModule onAiSaved={(cfg: unknown) => setAiCfg(cfg as ReturnType<typeof loadConfig>)} />}
        </div>
        </TopbarFocusProvider>

        {/* Footer */}
        {branding.footer_text && (
          <div style={{ background:'#F9FAFB', borderTop:'1px solid #E5E7EB', padding:'5px 24px', fontSize:11, color:'#9CA3AF', textAlign:'center' }}>
            {branding.footer_text}
          </div>
        )}
      </div>
    </div>

    <Dialog open={!!viewRecordUhid} onOpenChange={open => { if (!open) setViewRecordUhid(null); }}>
      {/* grid-cols-[minmax(0,1fr)]: DialogContent is a CSS grid, whose one
          column otherwise sizes to its widest child's min-content — the record
          then rendered wider than the dialog on a tablet and clipped its tabs,
          vitals and Actions menu off the right edge. Full-screen on phones. */}
      <DialogContent className="max-h-[88vh] w-[95vw] max-w-[1100px] grid-cols-[minmax(0,1fr)] gap-0 overflow-y-auto overflow-x-hidden p-0 max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0">
        <DialogHeader className="flex-row items-center justify-between gap-3 space-y-0 border-b border-border p-4 pr-11 text-left sm:p-5 sm:pr-11">
          <div>
            <DialogTitle>Patient record</DialogTitle>
            <DialogDescription className="max-sm:hidden">Overview, medications, history, results and documents.</DialogDescription>
          </div>
          {viewRecordUhid && (
            <button
              className="btn-ghost"
              style={{ padding: '5px 12px', fontSize: 12, flexShrink: 0 }}
              onClick={() => { viewPatientRecordInNewTab(viewRecordUhid); setViewRecordUhid(null); }}>
              ↗ Open in new tab
            </button>
          )}
        </DialogHeader>
        <div className="min-w-0 px-3 py-3 sm:px-5 sm:py-4">
          {viewRecordUhid && (
            <PatientRecords
              embedded
              preselectUhid={viewRecordUhid}
              onOpenVisit={visitCode => { setViewRecordUhid(null); setOpenVisitCode(visitCode); setMod('visit'); }}
              onOpenAdmission={row => { setViewRecordUhid(null); openAdmissionFromRecords(row, () => setViewRecordUhid(row.patient_uhid)); }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
    <Toaster />
    {user && <DoctorAlertPopup onOpenEncounter={id => { setErOpenEncounterId(id); setMod('emergency'); setMobileNavOpen(false); }} />}
    <MyAiPreferencesPanel open={showAiPreferences} onOpenChange={setShowAiPreferences} />
    <TwoStepDialog open={showTwoStep} onOpenChange={setShowTwoStep} username={user.username} />
    <ConfirmDialog
      open={confirmSignOut}
      onOpenChange={setConfirmSignOut}
      title="Sign out?"
      description="You will need to sign in again to continue. Anything not yet saved on this screen will be lost."
      confirmLabel="Sign out"
      variant="primary"
      onConfirm={logout}
    />
    {user && <FeedbackWidget moduleLabel={NAV.find(n => n.id === mod)?.label} />}
    {user && <CopilotLauncher onNavigate={openCopilotLink} />}
    </>
  );
}
